import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DERMA_CONDITIONS } from "@/lib/constants";
import { Plus, X, Star } from "lucide-react";

export default function DiagnosisBuilder({ diagnoses, onChange }) {
  const items = diagnoses || [];
  const [search, setSearch] = useState("");

  const add = (condition) => {
    const isPrimary = items.length === 0;
    onChange([...items, { condition, details: "", is_primary: isPrimary }]);
    setSearch("");
  };

  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    if (next.length > 0 && !next.some((d) => d.is_primary)) {
      next[0].is_primary = true;
    }
    onChange(next);
  };

  const update = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };

  const setPrimary = (idx) => {
    const next = items.map((d, i) => ({ ...d, is_primary: i === idx }));
    onChange(next);
  };

  const filtered = DERMA_CONDITIONS.filter((c) =>
    c.toLowerCase().includes(search.toLowerCase()) &&
    !items.some((d) => d.condition === c)
  );

  return (
    <div className="space-y-4">
      {/* Existing diagnoses */}
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((d, i) => (
            <div key={i} className={`flex items-start gap-2 rounded-lg border p-3 ${d.is_primary ? "border-gold bg-gold/5" : "border-navy/10 bg-white"}`}>
              <button onClick={() => setPrimary(i)} title={d.is_primary ? "Primary diagnosis" : "Set as primary"} className="mt-0.5">
                <Star className={`h-4 w-4 ${d.is_primary ? "fill-gold text-gold" : "text-muted-foreground/40"}`} />
              </button>
              <div className="min-w-0 flex-1 space-y-1.5">
                <p className="text-sm font-semibold text-navy">{d.condition}</p>
                <Input
                  placeholder="Details / elaboration..."
                  value={d.details || ""}
                  onChange={(e) => update(i, "details", e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              <button onClick={() => remove(i)} className="mt-0.5 rounded p-0.5 text-muted-foreground hover:text-red-500">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add diagnosis */}
      <div className="space-y-2">
        <Input
          placeholder="Search conditions or type custom..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && search.trim()) {
              e.preventDefault();
              add(search.trim());
            }
          }}
        />
        {search && (
          <div className="max-h-48 overflow-y-auto rounded-lg border border-navy/10 bg-white shadow-sm">
            {filtered.length > 0 ? (
              filtered.slice(0, 10).map((c) => (
                <button
                  key={c}
                  onClick={() => add(c)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-nude/40"
                >
                  <Plus className="h-3.5 w-3.5 text-gold" />
                  {c}
                </button>
              ))
            ) : (
              <button
                onClick={() => add(search.trim())}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gold hover:bg-nude/40"
              >
                <Plus className="h-3.5 w-3.5" />
                Add "{search.trim()}" as custom diagnosis
              </button>
            )}
          </div>
        )}
      </div>

      {items.length === 0 && !search && (
        <div className="rounded-lg border-2 border-dashed border-navy/10 py-8 text-center">
          <p className="text-sm text-muted-foreground">No diagnosis added yet.</p>
          <p className="text-xs text-muted-foreground">Search above or type a custom diagnosis.</p>
        </div>
      )}
    </div>
  );
}
